import React, {useContext, useState} from "react";
import {CAContext} from "./CAContext";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Box,
    Button,
    List,
    ListItemText,
    Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemitterForm from "./RemitterForm";
import RemitteeForm from "./RemitteeForm";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {getPatchValues} from "../utils/utils";

// Formik hands back {field: message}; nested shapes are flattened so nothing is lost.
function collectMessages(formikErrors) {
    const messages = [];
    const walk = (value) => {
        if (!value) return;
        if (typeof value === 'string') messages.push(value);
        else if (Array.isArray(value)) value.forEach(walk);
        else if (typeof value === 'object') Object.values(value).forEach(walk);
    };
    walk(formikErrors);
    return [...new Set(messages)];
}

export default function Entity(){
    const {caState} = useContext(CAContext);
    const {remitterRef, remitteeRef, setActiveStep} = caState.baseData;
    const [errors, setErrors] = useState(null);
    const [expanded, setExpanded] = useState({remitter: false, remittee: false});

    function toggleSection(section) {
        return (event, isExpanded) => setExpanded(current => ({...current, [section]: isExpanded}));
    }

    function handleEntitySave(){
        let remitter = remitterRef.current.validateForm();
        let remittee = remitteeRef.current.validateForm();

        Promise.all([remitter, remittee]).then((res) => {
            const sections = [
                {key: 'remitter', label: 'Remitter', messages: collectMessages(res[0])},
                {key: 'remittee', label: 'Remittee', messages: collectMessages(res[1])},
            ];
            // Part C is only for a remittance where 15CA applies; this used to block
            // the step with no message at all.
            if (remitterRef.current.values['is_ca_applicable'] === 'N'){
                sections[0].messages.push('15CA is marked not applicable - set "Is 15CA Applicable?" to YES and press Save');
            }
            // The Part C file is built from what is saved, and Next saves nothing, so an
            // edit left unsaved here would silently miss the JSON.
            const saved = caState.updateData || {};
            const unsaved = [
                getPatchValues(saved.remitter || {}, remitterRef.current.values),
                getPatchValues(saved.remittee || {}, remitteeRef.current.values),
            ];
            unsaved.forEach((changes, index) => {
                if (Object.keys(changes).length > 0) {
                    sections[index].messages.push('unsaved changes - press Save in this section first');
                }
            });
            const failed = sections.filter(section => section.messages.length > 0);

            if (failed.length === 0) {
                setErrors(null);
                setActiveStep(1, 0);
            } else {
                setErrors(failed);
                setExpanded(current => ({
                    ...current,
                    ...Object.fromEntries(failed.map(section => [section.key, true]))
                }));
            }
        });
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'column'
                }}
            >
                {
                    errors && <Alert
                        severity={'error'}
                        icon={false}
                        onClose={() => setErrors(null)}
                    >
                        <AlertTitle>Fix the following before continuing</AlertTitle>
                        <List disablePadding={true}>
                            {errors.map(section => (
                                <ListItemText key={section.key}>
                                    <b>{section.label}</b>: {section.messages.join('; ')}
                                </ListItemText>
                            ))}
                        </List>

                    </Alert>
                }
                <Accordion expanded={expanded.remitter} onChange={toggleSection('remitter')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography variant={'h5'}>Remitter</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <RemitterForm/>
                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={expanded.remittee} onChange={toggleSection('remittee')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography variant={'h5'}>Remittee</Typography>
                    </AccordionSummary
                    >
                    <AccordionDetails>
                        <RemitteeForm/>
                    </AccordionDetails>
                </Accordion>
                <Box
                    sx={{
                        textAlign: 'center'
                    }}
                >
                    <Button
                        variant={'contained'}
                        endIcon={<NavigateNextIcon/>}
                        onClick={handleEntitySave}
                    >
                        Next
                    </Button>
                </Box>
            </Box>
        </>
    )
}